import React, { useMemo, type ReactNode } from 'react'
import { Global, css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { MarginBox, Tab, Tabs } from '@fs/zion-ui'
import { useLocation, Link } from '@fs/zion-router'
import { getLanguage } from '@fs/zion-standards'
import { langText } from '@fs/zion-locale'
import { appPath } from '@fs/zion-config'
import Markdown from 'markdown-to-jsx'
import { TRAINING_NAV_ITEMS } from './PlaceEditorTrainingNav'
import useTrainingMarkdownContent from '../hooks/useTrainingMarkdownContent'
import { createMarkdownStyles } from '../../../assets/markdownStyles'
import ContentWrapper from '../../EditorHandbookPage/ContentWrapper'

const appBasePath = appPath ? new URL(appPath).pathname.replace(/\/$/, '') : ''

const markdownStyles = createMarkdownStyles('training-markdown')

function getLanguageTitle(locale: string): string {
  return (
    langText.core.find((l) => l.code === locale)?.text ||
    langText.additional.find((l) => l.code === locale)?.text ||
    getLanguage(locale)?.primaryText ||
    locale
  )
}

const IMG_SIZE_STYLES: Record<string, React.CSSProperties> = {
  'img-sm': { maxWidth: '60%', height: 'auto', display: 'block' },
  'img-xs': { maxWidth: '40%', height: 'auto', display: 'block' },
}

type MarkdownImgProps = Readonly<React.ImgHTMLAttributes<HTMLImageElement> & { class?: string }>

const MarkdownImg = ({ class: htmlClass, className, alt = '', style, src, ...props }: MarkdownImgProps) => {
  const cls = htmlClass || className || ''
  const sizeStyle = IMG_SIZE_STYLES[cls] ?? { maxWidth: '100%', height: 'auto' }
  const resolvedSrc = src?.startsWith('/place-editor-training/') ? `${appBasePath}${src}` : src
  return <img className={cls} alt={alt} style={{ ...sizeStyle, ...style }} src={resolvedSrc} {...props} />
}

type MarkdownLinkProps = Readonly<{
  href?: string
  children: ReactNode
}>

const MarkdownLink = ({ href, children, ...props }: MarkdownLinkProps) => {
  if (href?.startsWith('/')) {
    return (
      <Link to={href} {...props}>
        {children}
      </Link>
    )
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </a>
  )
}

function findItem(id: string) {
  if (!id) return TRAINING_NAV_ITEMS[0] || null
  const top = TRAINING_NAV_ITEMS.find((i) => i.id === id)
  if (top) return top
  const subItem = TRAINING_NAV_ITEMS.flatMap((parent) => parent.subItems || []).find((s) => s.id === id)
  return subItem || TRAINING_NAV_ITEMS[0] || null
}

const containerStyles = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: calc(100vh - 160px);
`

type TrainingMarkdownContentProps = Readonly<{
  mdFile: string
}>

function TrainingMarkdownContent({ mdFile }: TrainingMarkdownContentProps): JSX.Element | null {
  const [t] = useTranslation()
  const { markdown, locales, selectedLocale, setSelectedLocale, error, loading } = useTrainingMarkdownContent(mdFile)

  if (loading) return null

  if (error) {
    return (
      <MarginBox marginX="sm" marginY="sm">
        {t('place-editor-training.content.loading-error', 'Error loading content.')}
      </MarginBox>
    )
  }

  const showMarkdown = markdown && markdown.length > 0
  const index = locales?.indexOf(selectedLocale || '') ?? -1
  const selected = Math.max(index, 0)

  return (
    <>
      <Global styles={markdownStyles} />
      <ContentWrapper>
        <MarginBox marginX="sm" marginY="sm">
          {locales?.length > 1 && (
            <Tabs
              index={selected}
              onChange={(ind) => {
                const newLocale = locales[ind]
                setSelectedLocale(newLocale)
              }}
            >
              {locales.map((locale) => (
                <Tab key={locale} title={getLanguageTitle(locale)}>
                  {showMarkdown && (
                    <div className="training-markdown">
                      <Markdown options={{ overrides: { a: MarkdownLink, img: MarkdownImg } }}>{markdown}</Markdown>
                    </div>
                  )}
                </Tab>
              ))}
            </Tabs>
          )}
          {showMarkdown && locales.length === 1 && (
            <div className="training-markdown">
              <Markdown options={{ overrides: { a: MarkdownLink, img: MarkdownImg } }}>{markdown}</Markdown>
            </div>
          )}
        </MarginBox>
      </ContentWrapper>
    </>
  )
}

export default function PlaceEditorTrainingContent(): JSX.Element {
  const [t] = useTranslation()
  const location = useLocation()

  const hashId = useMemo(() => {
    const hash = location?.hash || ''
    return hash ? hash.substring(1) : ''
  }, [location?.hash])

  const currentItem = useMemo(() => findItem(hashId), [hashId])

  const mdFile = currentItem?.mdFile || null

  return (
    <div id="place-editor-training-content" css={containerStyles}>
      {mdFile ? (
        <TrainingMarkdownContent mdFile={mdFile} />
      ) : (
        <MarginBox marginX="sm" marginY="sm">
          <p style={{ margin: 0 }}>
            {t('place-editor-training.content.no-content-found', 'No content found for this topic.')}
          </p>
        </MarginBox>
      )}
    </div>
  )
}
